import React from 'react';

/* ━━━ 1. Venn Diagram ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface VennDiagramProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  overlapLabel?: string;
}
export const VennDiagram: React.FC<VennDiagramProps> = ({
  highlightTarget, labels = ['Set A','Set B'], accentColor = '#8b5cf6', opacity = 1, overlapLabel = 'Both',
}) => {
  const cx = 400, cy = 260, r = 120, sep = 70;
  const circles = [
    { x: cx - sep, y: cy, label: labels[0] || 'A', id: 'set_a' },
    { x: cx + sep, y: cy, label: labels[1] || 'B', id: 'set_b' },
    ...(labels[2] ? [{ x: cx, y: cy - sep * 0.9, label: labels[2], id: 'set_c' }] : []),
  ];
  return (
    <g opacity={opacity}>
      <defs>
        <filter id="vn-sh"><feDropShadow dx="2" dy="3" stdDeviation="4" floodOpacity="0.35"/></filter>
      </defs>
      {circles.map((c, i) => {
        const hi = highlightTarget === c.id;
        const hue = i * 40;
        return <g key={i} filter="url(#vn-sh)">
          <circle cx={c.x+3} cy={c.y+4} r={r} fill="rgba(0,0,0,0.2)"/>
          <circle cx={c.x} cy={c.y} r={r} fill={accentColor} opacity={hi ? 0.35 : 0.18} stroke={hi?'#fff':accentColor} strokeWidth={hi?3:1.5}
            style={{ filter: `hue-rotate(${hue}deg)` }}/>
          <text x={c.x + (i === 0 ? -50 : i === 1 ? 50 : 0)} y={c.y + (circles.length > 2 && i === 2 ? -50 : 5)}
            textAnchor="middle" fill="#e2e8f0" fontSize="13" fontFamily="Inter,sans-serif" fontWeight="600">{c.label}</text>
          {hi && <circle cx={c.x} cy={c.y} r={r+8} fill="none" stroke="#fff" strokeWidth="2" opacity="0.5">
            <animate attributeName="opacity" from="0.5" to="0" dur="1.2s" repeatCount="indefinite"/>
          </circle>}
        </g>;
      })}
      <text x={cx} y={cy + (circles.length > 2 ? 20 : 5)} textAnchor="middle" fill="#fff" fontSize="11" fontFamily="Inter,sans-serif" fontWeight="700">{overlapLabel}</text>
    </g>
  );
};

/* ━━━ 2. Balance Scale ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface BalanceScaleProps {
  highlightTarget?: string;
  labels?: Record<string, string>;
  accentColor?: string;
  opacity?: number;
  tilt?: number; // -1 to 1
}
export const BalanceScale: React.FC<BalanceScaleProps> = ({
  highlightTarget, labels = { left: 'Pro', right: 'Con' }, accentColor = '#f59e0b', opacity = 1, tilt = 0,
}) => {
  const cx = 400, baseY = 400, beamY = 160, beamLen = 260;
  const angle = tilt * 12; // degrees
  const leftHi = highlightTarget === 'left';
  const rightHi = highlightTarget === 'right';
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="bs-pole" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#475569"/><stop offset="50%" stopColor="#64748b"/><stop offset="100%" stopColor="#334155"/></linearGradient>
        <filter id="bs-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {/* Base */}
      <path d={`M${cx-50},${baseY} L${cx+50},${baseY} L${cx+20},${baseY-20} L${cx-20},${baseY-20}Z`} fill="#334155" filter="url(#bs-sh)"/>
      <line x1={cx-18} y1={baseY-19} x2={cx+18} y2={baseY-19} stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>
      {/* Pole */}
      <rect x={cx-6} y={beamY} width="12" height={baseY-beamY-20} fill="url(#bs-pole)" filter="url(#bs-sh)"/>
      {/* Fulcrum triangle */}
      <path d={`M${cx},${beamY-10} L${cx-14},${beamY+10} L${cx+14},${beamY+10}Z`} fill={accentColor} filter="url(#bs-sh)"/>
      {/* Beam (rotated) */}
      <g transform={`rotate(${angle},${cx},${beamY})`}>
        <rect x={cx-beamLen} y={beamY-4} width={beamLen*2} height="8" rx="4" fill="#475569" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
        <line x1={cx-beamLen+4} y1={beamY-3} x2={cx+beamLen-4} y2={beamY-3} stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
        {/* Left pan */}
        <g>
          <line x1={cx-beamLen+10} y1={beamY+4} x2={cx-beamLen+10} y2={beamY+60} stroke="#64748b" strokeWidth="1.5"/>
          <line x1={cx-beamLen+50} y1={beamY+4} x2={cx-beamLen+50} y2={beamY+60} stroke="#64748b" strokeWidth="1.5"/>
          <ellipse cx={cx-beamLen+30} cy={beamY+62} rx="50" ry="12" fill="#334155" stroke={leftHi?'#fff':accentColor} strokeWidth={leftHi?2:1}/>
          <ellipse cx={cx-beamLen+30} cy={beamY+58} rx="50" ry="12" fill="#1e293b"/>
          <text x={cx-beamLen+30} y={beamY+90} textAnchor="middle" fill="#e2e8f0" fontSize="12" fontFamily="Inter,sans-serif" fontWeight="600">{labels.left||'Left'}</text>
        </g>
        {/* Right pan */}
        <g>
          <line x1={cx+beamLen-50} y1={beamY+4} x2={cx+beamLen-50} y2={beamY+60} stroke="#64748b" strokeWidth="1.5"/>
          <line x1={cx+beamLen-10} y1={beamY+4} x2={cx+beamLen-10} y2={beamY+60} stroke="#64748b" strokeWidth="1.5"/>
          <ellipse cx={cx+beamLen-30} cy={beamY+62} rx="50" ry="12" fill="#334155" stroke={rightHi?'#fff':accentColor} strokeWidth={rightHi?2:1}/>
          <ellipse cx={cx+beamLen-30} cy={beamY+58} rx="50" ry="12" fill="#1e293b"/>
          <text x={cx+beamLen-30} y={beamY+90} textAnchor="middle" fill="#e2e8f0" fontSize="12" fontFamily="Inter,sans-serif" fontWeight="600">{labels.right||'Right'}</text>
        </g>
      </g>
    </g>
  );
};

/* ━━━ 3. Matrix Grid ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface MatrixGridProps {
  highlightTarget?: string;
  labels?: Record<string, string> | string[];
  rows?: string[];
  cols?: string[];
  accentColor?: string;
  opacity?: number;
}
export const MatrixGrid: React.FC<MatrixGridProps> = ({
  highlightTarget, rows = ['Row A','Row B','Row C'], cols = ['Col 1','Col 2','Col 3'],
  accentColor = '#06b6d4', opacity = 1,
}) => {
  const startX = 200, startY = 80, cellW = 120, cellH = 80;
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="mg-cell" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1e293b"/><stop offset="100%" stopColor="#0f172a"/></linearGradient>
        <filter id="mg-sh"><feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.3"/></filter>
      </defs>
      {/* Column headers */}
      {cols.map((c, ci) => <text key={`ch${ci}`} x={startX + ci * cellW + cellW/2} y={startY - 12} textAnchor="middle" fill={accentColor} fontSize="11" fontFamily="Inter,sans-serif" fontWeight="600">{c}</text>)}
      {/* Rows */}
      {rows.map((r, ri) => {
        const y = startY + ri * cellH;
        return <g key={`r${ri}`}>
          <text x={startX - 14} y={y + cellH/2 + 4} textAnchor="end" fill={accentColor} fontSize="11" fontFamily="Inter,sans-serif" fontWeight="600">{r}</text>
          {cols.map((_, ci) => {
            const x = startX + ci * cellW;
            const id = `cell_${ri}_${ci}`;
            const hi = highlightTarget === id;
            return <g key={`c${ri}${ci}`} filter="url(#mg-sh)">
              <rect x={x+2} y={y+3} width={cellW-4} height={cellH-4} rx="4" fill="rgba(0,0,0,0.25)"/>
              <rect x={x} y={y} width={cellW-4} height={cellH-4} rx="4" fill="url(#mg-cell)" stroke={hi?'#fff':accentColor} strokeWidth={hi?2:0.6} opacity={hi?1:0.8}/>
              {hi && <rect x={x} y={y} width={cellW-4} height={cellH-4} rx="4" fill={accentColor} opacity="0.15"/>}
            </g>;
          })}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 4. Stacked Bars ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface StackedBarsProps {
  highlightTarget?: string;
  labels?: string[];
  values?: number[];
  accentColor?: string;
  opacity?: number;
}
export const StackedBars: React.FC<StackedBarsProps> = ({
  highlightTarget, labels = ['Cat A','Cat B','Cat C'], values = [0.6, 0.8, 0.5],
  accentColor = '#22c55e', opacity = 1,
}) => {
  const startX = 160, maxW = 440, barH = 50, gap = 20, startY = 100;
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="sb-bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1e293b"/><stop offset="100%" stopColor="#0f172a"/></linearGradient>
        <filter id="sb-sh"><feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.3"/></filter>
      </defs>
      {labels.map((label, i) => {
        const y = startY + i * (barH + gap);
        const w = maxW * (values[i] ?? 0.5);
        const id = `bar_${i+1}`;
        const hi = highlightTarget === id;
        const hue = i * 30;
        return <g key={i}>
          {/* Track */}
          <rect x={startX} y={y} width={maxW} height={barH} rx="6" fill="url(#sb-bg)" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
          {/* Bar shadow */}
          <rect x={startX+3} y={y+3} width={w} height={barH} rx="6" fill="rgba(0,0,0,0.3)"/>
          {/* Bar */}
          <rect x={startX} y={y} width={w} height={barH} rx="6" fill={accentColor} opacity={hi?0.9:0.6}
            stroke={hi?'#fff':'none'} strokeWidth="2" style={{ filter: `hue-rotate(${hue}deg)` }} filter="url(#sb-sh)"/>
          {/* Specular */}
          <line x1={startX+6} y1={y+4} x2={startX+w-6} y2={y+4} stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
          <text x={startX - 10} y={y + barH/2 + 4} textAnchor="end" fill="#e2e8f0" fontSize="11" fontFamily="Inter,sans-serif" fontWeight="500">{label}</text>
          <text x={startX + w + 10} y={y + barH/2 + 4} fill="#94a3b8" fontSize="10" fontFamily="Inter,sans-serif">{Math.round((values[i]??0.5)*100)}%</text>
        </g>;
      })}
    </g>
  );
};

/* ━━━ 5. Pie Segments ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface PieSegmentsProps {
  highlightTarget?: string;
  labels?: string[];
  values?: number[];
  accentColor?: string;
  opacity?: number;
}
export const PieSegments: React.FC<PieSegmentsProps> = ({
  highlightTarget, labels = ['Part A','Part B','Part C'], values,
  accentColor = '#e11d48', opacity = 1,
}) => {
  const cx = 400, cy = 260, r = 140;
  const n = labels.length;
  const sliceVals = values || labels.map((_, i) => 1 / n + (i === 0 ? 0.1 : -0.1 / (n - 1)));
  const total = sliceVals.reduce((s, v) => s + v, 0);
  let accAngle = -Math.PI / 2;
  return (
    <g opacity={opacity}>
      <defs><filter id="ps-sh"><feDropShadow dx="2" dy="3" stdDeviation="4" floodOpacity="0.35"/></filter></defs>
      {/* Shadow */}
      <ellipse cx={cx+4} cy={cy+5} rx={r} ry={r} fill="rgba(0,0,0,0.3)"/>
      {labels.map((label, i) => {
        const angle = (sliceVals[i] / total) * 2 * Math.PI;
        const startA = accAngle;
        accAngle += angle;
        const endA = accAngle;
        const x1 = cx + r * Math.cos(startA), y1 = cy + r * Math.sin(startA);
        const x2 = cx + r * Math.cos(endA), y2 = cy + r * Math.sin(endA);
        const large = angle > Math.PI ? 1 : 0;
        const id = `slice_${i+1}`;
        const hi = highlightTarget === id;
        const hue = i * (360 / n);
        const midA = startA + angle / 2;
        const lx = cx + (r + 24) * Math.cos(midA), ly = cy + (r + 24) * Math.sin(midA);
        return <g key={i} filter="url(#ps-sh)">
          <path d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2}Z`}
            fill={accentColor} opacity={hi?0.9:0.55} stroke={hi?'#fff':'rgba(0,0,0,0.3)'} strokeWidth={hi?2.5:1}
            style={{ filter: `hue-rotate(${hue}deg)` }}/>
          <text x={lx} y={ly+4} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontFamily="Inter,sans-serif" fontWeight="500">{label}</text>
        </g>;
      })}
      {/* Center hole (donut) */}
      <circle cx={cx} cy={cy} r={r * 0.45} fill="#0f172a"/>
      <circle cx={cx} cy={cy} r={r * 0.45} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
    </g>
  );
};
